import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import StatsCard from '../components/shared/StatsCard';
import AdminReports from '../components/admin/AdminReports';
import ConvenerManagement from '../components/admin/ConvenerManagement';
import AllMembersTable from '../components/admin/AllMembersTable';
import AllMeetingsTable from '../components/admin/AllMeetingsTable';
import AssignMeetingModal from '../components/admin/AssignMeetingModal';
import MeetingTypesManagement from '../components/admin/MeetingTypesManagement';
import LoadingState from '../components/shared/LoadingState';
import { useAdmin } from '../hooks/useAdmin';
import { Users, Calendar, UserCheck, ClipboardList, RefreshCw, Plus, TrendingUp, CheckSquare, Settings } from 'lucide-react';

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'conveners' | 'members' | 'meetings' | 'reports' | 'types'>('dashboard');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const { stats, loading, refresh } = useAdmin();

  if (loading && activeTab === 'dashboard') {
    return (
      <Layout title="Admin Dashboard">
        <LoadingState title="Loading Admin Dashboard & System Analytics..." minHeight="min-h-[400px]" />
      </Layout>
    );
  }

  const navTabs = [
    { id: 'dashboard', label: 'Overview', icon: TrendingUp },
    { id: 'conveners', label: 'Conveners', icon: UserCheck },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'meetings', label: 'Meetings', icon: Calendar },
    { id: 'types', label: 'Types', icon: Settings },
    { id: 'reports', label: 'Reports', icon: ClipboardList },
  ];

  return (
    <Layout 
      title="Admin Control Center" 
      tabs={navTabs}
      activeTab={activeTab}
      onTabChange={(id: string) => setActiveTab(id as any)}
    >
      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-8">
          {/* Admin Hero Section */}
          <div className="relative bg-gradient-to-br from-indigo-700 to-purple-800 rounded-3xl p-6 sm:p-10 shadow-lg shadow-indigo-500/20 overflow-hidden">
            {/* Decorative background blobs */}
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-72 h-72 bg-white opacity-5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-20 w-48 h-48 bg-purple-400 opacity-20 rounded-full blur-2xl"></div>
            
            <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              <div className="max-w-xl">
                <h2 className="text-2xl sm:text-3xl font-black text-white mb-2 tracking-tight">System Overview</h2>
                <p className="text-indigo-100 font-medium text-sm sm:text-base leading-relaxed">
                  Monitor overall system performance, manage convener assignments, and track state-wide attendance completion.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                <button
                  onClick={refresh}
                  className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-3 sm:py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl backdrop-blur-md border border-white/20 font-bold text-sm transition-all shadow-sm"
                  title="Refresh System Data"
                >
                  <RefreshCw size={18} />
                  <span className="sm:hidden">Refresh Data</span>
                </button>
                <button
                  onClick={() => setShowAssignModal(true)}
                  className="w-full sm:w-auto flex items-center justify-center space-x-2 px-6 py-3 sm:py-3.5 bg-white text-indigo-700 rounded-xl hover:bg-indigo-50 font-black text-sm shadow-xl shadow-indigo-900/20 transition-all hover:-translate-y-1"
                >
                  <Plus size={20} className="stroke-[3px]" />
                  <span>Assign Convener</span>
                </button>
              </div>
            </div>
          </div>

          {/* Primary System KPI Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <StatsCard title="Total Conveners" value={stats?.totalConveners || 0} icon={UserCheck} color="blue" subtext="Active users" />
            <StatsCard title="Total Members" value={stats?.totalMembers || 0} icon={Users} color="green" subtext="Across all types" />
            <StatsCard title="Total Meetings" value={stats?.totalMeetings || 0} icon={Calendar} color="purple" subtext="All time instances" />
            <StatsCard title="System Attendance Rate" value={`${stats?.overallAttendanceRate || 0}%`} icon={TrendingUp} color="orange" subtext="Global average" />
          </div>

          {/* System Health Highlights Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Meeting Completion Rate</div>
                <div className="text-2xl font-bold text-blue-900 mt-1">{stats?.completionRate || 0}% Completed</div>
                <div className="text-[11px] text-blue-700 mt-0.5">{stats?.completedMeetings || 0} out of {stats?.totalMeetings || 0} meetings finished</div>
              </div>
              <div className="p-3 bg-blue-600 text-white rounded-xl shadow-xs">
                <CheckSquare size={22} />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-green-800 uppercase tracking-wide">Meetings This Month</div>
                <div className="text-2xl font-bold text-green-900 mt-1">{stats?.meetingsThisMonth || 0} Meetings</div>
                <div className="text-[11px] text-green-700 mt-0.5">Held during current month</div>
              </div>
              <div className="p-3 bg-green-600 text-white rounded-xl shadow-xs">
                <Calendar size={22} />
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-purple-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-purple-800 uppercase tracking-wide">Attendance Entries</div>
                <div className="text-2xl font-bold text-purple-900 mt-1">{stats?.totalAttendanceRecords || 0} Records</div>
                <div className="text-[11px] text-purple-700 mt-0.5">{stats?.totalPresent || 0} Present • {stats?.totalAbsent || 0} Absent</div>
              </div>
              <div className="p-3 bg-purple-600 text-white rounded-xl shadow-xs">
                <ClipboardList size={22} />
              </div>
            </div>
          </div>

          {/* Meeting Type Performance Overview */}
          {stats?.meetingTypeBreakdown && stats.meetingTypeBreakdown.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base sm:text-lg font-bold text-gray-900">Meeting Type Analytics</h2>
                <button
                  onClick={() => setActiveTab('types')}
                  className="text-xs font-bold text-blue-600 hover:text-blue-800"
                >
                  Manage Types
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.meetingTypeBreakdown.map((item: any) => (
                  <div key={item.id} className="p-4 border border-gray-200 rounded-xl bg-gray-50/50 hover:bg-gray-50 transition space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-bold text-gray-900 text-sm sm:text-base">{item.name}</div>
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 inline-block mt-1">
                          {item.level} Level
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500 font-medium">Avg Rate</div>
                        <div className={`text-base font-extrabold ${
                          item.attendanceRate >= 75 ? 'text-green-600' :
                          item.attendanceRate >= 50 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {item.attendanceRate}%
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          item.attendanceRate >= 75 ? 'bg-green-500' :
                          item.attendanceRate >= 50 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${item.attendanceRate}%` }}
                      ></div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500 font-medium pt-1">
                      <span>{item.meetingsCount} Meetings</span>
                      <span>{item.membersCount} Members</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent System Activity Feed */}
          {stats?.recentMeetings && stats.recentMeetings.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base sm:text-lg font-bold text-gray-900">Recent Meetings Across System</h2>
                <button
                  onClick={() => setActiveTab('meetings')}
                  className="text-xs font-bold text-blue-600 hover:text-blue-800"
                >
                  View All Meetings ({stats.totalMeetings})
                </button>
              </div>

              <div className="space-y-3">
                {stats.recentMeetings.map((inst: any) => (
                  <div key={inst.id} className="p-3.5 border border-gray-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-gray-50 transition">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-bold text-gray-900 text-xs sm:text-sm">{inst.meeting_types?.name}</span>
                        <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-blue-100 text-blue-800">
                          {inst.meeting_types?.level}
                        </span>
                        <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${
                          inst.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          inst.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {inst.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Convener: <span className="font-semibold text-gray-700">{inst.users?.name || 'Unassigned'}</span> • Date: {inst.date} {inst.time && `(${inst.time})`}
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 self-end sm:self-center">
                      <div className="text-right">
                        <div className="text-xs font-bold text-gray-900">
                          {inst.attendanceSummary?.present || 0} / {inst.attendanceSummary?.total || 0} Present
                        </div>
                        <div className="text-[11px] text-gray-500 font-medium">
                          {inst.attendanceSummary?.rate || 0}% Attendance
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Conveners Tab */}
      {activeTab === 'conveners' && <ConvenerManagement />}

      {/* Members Tab */}
      {activeTab === 'members' && <AllMembersTable />}

      {/* Meetings Tab */}
      {activeTab === 'meetings' && (
        <div>
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => setShowAssignModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-semibold text-xs sm:text-sm"
            >
              <Plus size={16} />
              <span>Assign Meeting</span>
            </button>
          </div>
          <AllMeetingsTable />
        </div>
      )}

      {/* Meeting Types Tab */}
      {activeTab === 'types' && <MeetingTypesManagement />}

      {/* Reports Tab */}
      {activeTab === 'reports' && <AdminReports />}

      {/* Assign Meeting Modal */}
      <AssignMeetingModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onSuccess={() => {
          refresh();
          setShowAssignModal(false);
        }}
      />
    </Layout>
  );
}
